<template>
  <div class="subscription-landing subscription-landing--champagne">
    <section class="hero">
      <div class="site-width">
        <div class="hero-content">
          <h1 class="clubname">Champagne Club</h1>
          <p class="club-subhead">
            Champagne Brilliance.
            <br />
            No Occasion Necessary.
          </p>
          <p>
            <a class="button primary" @click.prevent="signup()">{{
              ctaTextShort
            }}</a>
          </p>
          <p v-if="hasActiveGiftTerm">
            <span class="gift-alternative">
              <nuxt-link to="/wine-club/gifting/champagne-club/" class="button">
                Or gift it
              </nuxt-link>
            </span>
          </p>
        </div>
      </div>
    </section>
    <section class="champagne-houses">
      <div class="site-width text-center">
        <div class="row xlarge-flex">
          <div class="columns large-6 side-image hide-for-medium-down">
            <img
              src="~assets/images/club-champagne-bottle-rack.png"
              class="bottlerack"
              alt="Wine bottles in rack"
            />
          </div>
          <div class="columns large-6 text-center large-text-left">
            <p class="max-copy section-heading">
              Elite Champagne Houses—
              <br />
              From Historic to Boutique.
            </p>
            <p>
              The heft of the bottle. The pop of the cork. The silky mousse and
              the scintillating texture. These are the things we love about
              Champagne, and they’re why we’ve always wanted to have a Champagne
              club—so we could hand-pick our favorite bottles to share with Wine
              Access members.
            </p>
            <p>
              Every glass of Champagne is a theatrical, romantic experience, and
              each of the bottles you’ll get as a member of the Wine Access
              Champagne Club represents the best of one of Earth’s most hallowed
              wine regions. Whether it’s a distinguished icon that has written
              Champagne history or a boutique bottle from one of the
              grower-producers defining the region right now, you’ll relish
              every last sip, just like we do.
            </p>
          </div>
        </div>
        <p class="join-club">
          <a class="button primary" @click="signup()">
            {{ ctaTextLong }}
          </a>
        </p>
        <p v-if="hasActiveGiftTerm">
          <span class="gift-alternative">
            <nuxt-link to="/wine-club/gifting/champagne-club/" class="button">
              Or gift it
            </nuxt-link>
          </span>
        </p>
      </div>
    </section>
    <section class="shaded member-benefits">
      <div class="site-width text-center">
        <div>
          <h2 class="benefits">Member Benefits</h2>
          <p>
            Each bottle is hand-selected by a team of industry experts,
            including a Master Sommelier, International Wine Judge and other
            industry experts.
          </p>
        </div>
        <div class="row icon-grid">
          <div class="columns medium-6 large-3">
            <div class="icon">
              <img
                src="~assets/images/wa-icon-club-discount.svg"
                alt="Percent Symbol"
                class="icon-desktop"
              />
              <img
                src="~assets/images/wa-icon-club-discount-mobile.svg"
                alt="Percent Symbol"
                class="icon-mobile"
              />
            </div>
            <p>10% off all Wine Access purchases *</p>
          </div>
          <div class="columns medium-6 large-3">
            <div class="icon">
              <img
                src="~assets/images/wa-icon-club-video.svg"
                alt="Video Camera Icon"
                class="icon-desktop"
              />
              <img
                src="~assets/images/wa-icon-club-video-mobile.svg"
                alt="Video Camera Icon"
                class="icon-mobile"
              />
            </div>
            <p>Exclusive Tasting Videos</p>
          </div>
          <div class="columns medium-6 large-3">
            <div class="icon">
              <img
                src="~assets/images/wa-icon-club-bottle.svg"
                alt="Wine Glass Icon"
                class="icon-desktop"
              />
              <img
                src="~assets/images/wa-icon-club-bottle-mobile.svg"
                alt="Wine Glass Icon"
                class="icon-mobile"
              />
            </div>
            <p>Exclusive access to highly limited cuvées</p>
          </div>
          <div class="columns medium-6 large-3">
            <div class="icon">
              <img
                src="~assets/images/wa-icon-club-shipping.svg"
                alt="Box Icon"
                class="icon-desktop"
              />
              <img
                src="~assets/images/wa-icon-club-shipping-mobile.svg"
                alt="Box Icon"
                class="icon-mobile"
              />
            </div>
            <p>Complimentary shipping</p>
          </div>
        </div>
      </div>
    </section>
    <section>
      <div class="site-width text-center">
        <BreadcrumbComponent :breadcrumbs="breadcrumbs" />
        <div>
          <p class="section-heading">
            Luxury. Rarity. History.
            <br />
            At Your Door.
          </p>
        </div>
        <div>
          <div class="bottle-selection sparkling">
            <div class="bottle"></div>
            <div class="bottle"></div>
            <div class="bottle"></div>
            <div class="bottle"></div>
          </div>
          <div class="club-selector">
            <div class="option">
              <p for="club-sparkling">All Sparkling</p>
            </div>
          </div>
          <div class="bordered-callout">
            <p>
              $200 – 250 plus tax per 4-bottle shipment.
              <br />
              Delivered quarterly.
            </p>
          </div>
        </div>
        <p>
          <a class="button primary" @click="signup()">{{ ctaTextLong }}</a>
        </p>
        <p v-if="hasActiveGiftTerm">
          <span class="gift-alternative">
            <nuxt-link to="/wine-club/gifting/champagne-club/" class="button">
              Or gift it
            </nuxt-link>
          </span>
        </p>
      </div>
    </section>
  </div>
</template>

<script>
import ga4 from '#common/utils/ga4'
import { getClubCrumbs } from '#common/utils/wineclub'
import BreadcrumbComponent from '~/components/Breadcrumb.vue'

export default {
  name: 'ChampagneClubIndex',
  components: {
    BreadcrumbComponent,
  },
  async setup() {
    const { data } = await useAsyncData('champagne-club', () =>
      $fetch('/api/membership/champagne-club'),
    )
    return {
      data,
      isFull: computed(() => data.value?.plans?.every((p) => p.is_full)),
      hasActiveGiftTerm: computed(() => data.value?.has_active_gift_term),
      breadcrumbs: getClubCrumbs('Champagne Club'),
    }
  },
  computed: {
    ctaTextShort() {
      if (this.isFull) {
        return 'Join The Waiting List'
      } else {
        return 'Sign Me Up'
      }
    },
    ctaTextLong() {
      if (this.isFull) {
        return 'Join The Waiting List'
      } else {
        return 'Join the Champagne Club'
      }
    },
  },
  mounted() {
    ga4.trackViewClub({ slug: 'champagne-club' })
  },
  methods: {
    openLoginModal() {
      this.$auth.openAuthModal({
        redirect: '/champagne-club/join/',
        isClub: true,
      })
    },

    signup() {
      if (!this.$auth.loggedIn) {
        this.openLoginModal()
      } else {
        this.$router.push('/champagne-club/join/')
      }
    },
  },
}
</script>
